import { useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/Button";
import {
  isChunkLoadError,
  reloadWithGuard,
  tryChunkReload,
} from "@/lib/chunkReload";
import { assignPage, reloadPage } from "@/lib/reloadPage";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";

interface Props {
  error: unknown;
  /**
   * Fill the viewport (no shell around it — public pages, guard failures).
   * Inside a shell the screen only fills the content area.
   */
  fullScreen?: boolean;
}

function homePathForRole(role: string | undefined): string {
  if (role === "superadmin") return "/admin";
  if (role === "dealer") return "/dealer";
  return "/";
}

function devMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

/**
 * BUG-745 — branded replacement for React Router's developer error page.
 *
 * A failed lazy-chunk load (stale tab after a deploy) gets «the app was
 * updated» plus ONE guarded automatic reload; any other route error gets a
 * generic screen with «reload» and «home». Both buttons are full page loads on
 * purpose: after an error the in-memory app state is not trustworthy, and a
 * full load is what fetches the new chunk names.
 */
export function AppErrorScreen({ error, fullScreen = false }: Props) {
  const { t } = useTranslation();
  const role = useAuthStore((s) => s.user?.role);
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (chunkError) tryChunkReload();
  }, [chunkError]);

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-4 px-6 text-center",
        fullScreen ? "min-h-screen bg-gray-50" : "min-h-[60vh]",
      )}
    >
      {chunkError ? (
        <RefreshCw size={40} className="text-brand-primary" aria-hidden />
      ) : (
        <AlertTriangle size={40} className="text-brand-warning" aria-hidden />
      )}
      <h1 className="text-xl font-semibold text-gray-800">
        {chunkError ? t("errors.chunk.title") : t("errors.route.title")}
      </h1>
      <p className="max-w-md text-sm text-gray-500">
        {chunkError ? t("errors.chunk.subtitle") : t("errors.route.subtitle")}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3">
        <Button onClick={() => (chunkError ? reloadWithGuard() : reloadPage())}>
          <RefreshCw size={16} aria-hidden />
          {t("errors.route.reload")}
        </Button>
        {!chunkError && (
          <Button
            variant="secondary"
            onClick={() => assignPage(homePathForRole(role))}
          >
            {t("errors.goToDashboard")}
          </Button>
        )}
      </div>
      {import.meta.env.DEV && (
        <p className="max-w-xl break-words font-mono text-xs text-gray-400">
          {devMessage(error)}
        </p>
      )}
    </div>
  );
}
